import React, {
  ComponentType,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useStatusCheck } from "./hooks";
import { CheckedEnvironment } from "./types";
import { CheckedMobileApp } from "./mobile-apps/types";
import {
  readRefreshInterval,
  RefreshIntervalMs,
  writeRefreshInterval,
} from "./refresh-interval";

type StatusRefreshValue = {
  environments: CheckedEnvironment[];
  mobileApps: CheckedMobileApp[];
  lastCheckedDate?: Date;
  checkGeneration: number;
  refreshIntervalMs: RefreshIntervalMs;
  setRefreshIntervalMs: (value: RefreshIntervalMs) => void;
  refresh: () => void;
};

const StatusRefreshContext = createContext<StatusRefreshValue | null>(null);

/**
 * Owns status polling above route pages so navigation does not remount
 * the refresh loop or re-trigger the initial fetch.
 */
export function StatusRefreshProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { data, mobileApps, lastCheckedDate, checkGeneration, performCheck } =
    useStatusCheck(true);
  const [refreshIntervalMs, setRefreshIntervalMsState] =
    useState<RefreshIntervalMs>(() => readRefreshInterval());

  useEffect(() => {
    if (refreshIntervalMs <= 0) return;

    const id = setInterval(performCheck, refreshIntervalMs);
    return () => {
      clearInterval(id);
    };
  }, [performCheck, refreshIntervalMs]);

  const setRefreshIntervalMs = useCallback((value: RefreshIntervalMs) => {
    setRefreshIntervalMsState(value);
    writeRefreshInterval(value);
  }, []);

  const value = useMemo(
    () => ({
      environments: data,
      mobileApps,
      lastCheckedDate,
      checkGeneration,
      refreshIntervalMs,
      setRefreshIntervalMs,
      refresh: performCheck,
    }),
    [
      data,
      mobileApps,
      lastCheckedDate,
      checkGeneration,
      refreshIntervalMs,
      setRefreshIntervalMs,
      performCheck,
    ],
  );

  return (
    <StatusRefreshContext.Provider value={value}>
      {children}
    </StatusRefreshContext.Provider>
  );
}

export function useStatusRefresh(): StatusRefreshValue {
  const ctx = useContext(StatusRefreshContext);
  if (!ctx) {
    throw new Error(
      "useStatusRefresh must be used within StatusRefreshProvider",
    );
  }
  return ctx;
}

/** HOC that keeps status refreshing alive across Environments / Apps pages. */
export function withStatusRefresh<P extends object>(
  Component: ComponentType<P>,
) {
  function WithStatusRefresh(props: P) {
    return (
      <StatusRefreshProvider>
        <Component {...props} />
      </StatusRefreshProvider>
    );
  }

  WithStatusRefresh.displayName = `withStatusRefresh(${
    Component.displayName || Component.name || "Component"
  })`;

  return WithStatusRefresh;
}
